import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Production from "@/pages/Production";
import PenProduction from "@/pages/PenProduction";
import FeedInventory from "@/pages/FeedInventory";
import ChickenInventory from "@/pages/ChickenInventory";
import Customers from "@/pages/Customers";
import Invoices from "@/pages/Invoices";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import EmptyFeedBags from "@/pages/EmptyFeedBags";
import OrderRequest from "@/pages/OrderRequest";
import DistributorManagement from "@/pages/DistributorManagement";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import CustomerAccountsSheet from "@/pages/CustomerAccountsSheet";
import VetDoctorReport from "@/pages/VetDoctorReport";
import AuditorReport from "@/pages/AuditorReport";
import DailyFarmOperationsReport from "@/pages/DailyFarmOperationsReport";
import DistributorStockReports from "@/pages/DistributorStockReports";
import OrderNotificationToFarm from "@/pages/OrderNotificationToFarm";
import PayrollManagement from "@/pages/PayrollManagement";
import PaySlip from "@/pages/PaySlip";
import Forms from "@/pages/Forms";
import TimeSheet from "@/pages/TimeSheet";
import Messages from "@/pages/Messages";
import Directory from "@/pages/Directory";
import Deposits from "@/pages/Deposits";
import PaymentsCharges from "@/pages/PaymentsCharges";
import AccessBankReconciliation from "@/pages/AccessBankReconciliation";
import ZenithBankReconciliation from "@/pages/ZenithBankReconciliation";
import OtherBankReconciliation from "@/pages/OtherBankReconciliation";
import JobApplicationPublic from "@/pages/JobApplicationPublic";
import Register from "@/pages/Register";
import ApplicationStatus from "@/pages/ApplicationStatus";
import JobApplicationQR from "@/pages/JobApplicationQR";
import PriceManagement from "@/pages/PriceManagement";
import CustomerAccountRecords from "@/pages/CustomerAccountRecords";
import CustomerOrderPickup from "@/pages/CustomerOrderPickup";
import DailyEggProductionByPen from "@/pages/DailyEggProductionByPen";
import PenFloorDailyEggProduction from "@/pages/PenFloorDailyEggProduction";
import PenAssignments from "@/pages/PenAssignments";
import ProductionReports from "@/pages/ProductionReports";
import ProductionsEdit from "@/pages/ProductionsEdit";
import DistributorCommissionLedger from "@/pages/DistributorCommissionLedger";
import FileManager from "@/pages/FileManager";
import ChickenFeedAudit from "@/pages/ChickenFeedAudit";
import ChickenCensus from "@/pages/ChickenCensus";
import FrozenChickenCount from "@/pages/FrozenChickenCount";
import CustomerPlaceOrder from "@/pages/CustomerPlaceOrder";
import CustomerMessages from "@/pages/CustomerMessages";
import NotificationHistory from "@/pages/NotificationHistory";
import OrderPickupSummary from "@/pages/OrderPickupSummary";
import PickupAnalytics from "@/pages/PickupAnalytics";
import SalesAnalysis from "@/pages/SalesAnalysis";
import Layout from "@/components/Layout";
import MessagePopup from "@/components/MessagePopup";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!(localStorage.getItem("token") && localStorage.getItem("user"));
  });
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setIsAuthenticated(true);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setUser(null);
  };

  // Helper function to check if user can access a specific bank reconciliation tab
  // Admin: always. Manager: only if explicitly granted via allowed_tabs (view-only enforced server-side)
  const canAccessBankTab = (user, path) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role !== 'manager') return false;
    const legacyManagers = ['Waliu Aina', 'Rashidat Aina'];
    return (user.allowed_tabs || []).includes(path) || legacyManagers.includes(user.name);
  };

  // Helper function to check if Support Staff has access to a specific tab
  const supportStaffHasAccess = (tabPath) => {
    if (user?.role !== 'support_staff') return true; // Non-support_staff handled by other checks
    if (tabPath === '/payslip' || tabPath === '/profile') return true; // Always allowed
    return user?.allowed_tabs?.includes(tabPath) || false;
  };

  // Helper function to check if Staff has access to a specific tab (for tabs that require assignment)
  const staffHasAccess = (tabPath) => {
    if (user?.role !== 'staff') return true; // Non-staff handled by other checks
    return user?.allowed_tabs?.includes(tabPath) || false;
  };

  return (
    <div className="App">
      <CurrencyProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              }
            />
          {/* Public Job Application Route - No auth required */}
          <Route path="/apply" element={<JobApplicationPublic />} />
          <Route path="/apply/status" element={<ApplicationStatus />} />
          {/* Public role-scoped registration - No auth required */}
          <Route path="/register/:role" element={<Register />} />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                user?.role === 'customer' ? (
                  <Navigate to="/order-request" replace />
                ) : user?.role === 'distributor' ? (
                  <Navigate to="/order-request" replace />
                ) : user?.role === 'vet_doctor' ? (
                  <Navigate to="/vet-reports" replace />
                ) : user?.role === 'auditor' ? (
                  <Navigate to="/auditor-reports" replace />
                ) : user?.role === 'support_staff' ? (
                  <Navigate to="/payslip" replace />
                ) : user?.role === 'staff' && user?.allowed_tabs?.length > 0 ? (
                  <Navigate to={user.allowed_tabs[0]} replace />
                ) : (user?.role === 'manager' || user?.role === 'admin') ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <Dashboard user={user} />
                  </Layout>
                ) : (
                  <Navigate to="/pen-production" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/production"
            element={
              isAuthenticated && (user?.role !== 'support_staff' || supportStaffHasAccess('/production')) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Production />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/pen-production"
            element={
              isAuthenticated && (user?.role !== 'support_staff' || supportStaffHasAccess('/pen-production')) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PenProduction />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/inventory"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <FeedInventory />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/chicken-inventory"
            element={
              isAuthenticated && (user?.role === 'manager' || user?.role === 'admin') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ChickenInventory />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customers"
            element={
              isAuthenticated && (user?.role === 'manager' || user?.role === 'admin') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Customers />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customer-place-order"
            element={
              isAuthenticated && user?.role === 'customer' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerPlaceOrder />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customer-messages"
            element={
              isAuthenticated && user?.role === 'customer' ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerMessages />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/invoices"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Invoices />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Reports />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/daily-farm-operations-report"
            element={
              isAuthenticated ? (
                ['admin', 'manager', 'staff', 'support_staff'].includes(user?.role) ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <DailyFarmOperationsReport />
                  </Layout>
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/distributor-stock-reports"
            element={
              isAuthenticated ? (
                ['admin', 'manager', 'distributor'].includes(user?.role) ? (
                  <Layout user={user} onLogout={handleLogout}>
                    <DistributorStockReports />
                  </Layout>
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/deposits"
            element={
              isAuthenticated && (user?.role === 'manager' || user?.role === 'admin') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Deposits />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payments-charges"
            element={
              isAuthenticated && (user?.role === 'manager' || user?.role === 'admin') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PaymentsCharges />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/access-bank"
            element={
              isAuthenticated && canAccessBankTab(user, '/access-bank') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AccessBankReconciliation />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/zenith-bank"
            element={
              isAuthenticated && canAccessBankTab(user, '/zenith-bank') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ZenithBankReconciliation />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/other-bank"
            element={
              isAuthenticated && canAccessBankTab(user, '/other-bank') ? (
                <Layout user={user} onLogout={handleLogout}>
                  <OtherBankReconciliation />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Users />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/empty-feed-bags"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <EmptyFeedBags />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/order-request"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <OrderRequest />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/distributors"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DistributorManagement />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/distributor-commission"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "distributor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DistributorCommissionLedger />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/file-manager"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <FileManager />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/chicken-feed-audit"
            element={
              isAuthenticated && (
                user?.role === 'admin' || 
                user?.role === 'manager' || 
                (user?.role === 'staff' && staffHasAccess('/chicken-feed-audit')) ||
                (user?.role === 'support_staff' && supportStaffHasAccess('/chicken-feed-audit'))
              ) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ChickenFeedAudit />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/chicken-census"
            element={
              isAuthenticated && (
                user?.role === 'admin' || 
                user?.role === 'manager' || 
                (user?.role === 'staff' && (staffHasAccess('/chicken-census') || user?.assigned_pens?.length > 0)) ||
                (user?.role === 'support_staff' && supportStaffHasAccess('/chicken-census'))
              ) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ChickenCensus />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/frozen-chicken-count"
            element={
              isAuthenticated && (
                user?.role === 'admin' || 
                user?.role === 'manager' || 
                (user?.role === 'staff' && staffHasAccess('/frozen-chicken-count')) ||
                (user?.role === 'support_staff' && supportStaffHasAccess('/frozen-chicken-count'))
              ) ? (
                <Layout user={user} onLogout={handleLogout}>
                  <FrozenChickenCount />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Profile />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/notification-history"
            element={
              isAuthenticated ? (
                <Layout user={user} onLogout={handleLogout}>
                  <NotificationHistory />
                </Layout>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Settings />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customer-accounts"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerAccountsSheet />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/vet-reports"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "vet_doctor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <VetDoctorReport />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/auditor-reports"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "auditor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <AuditorReport />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/order-notifications"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "distributor" || user?.role === "staff") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <OrderNotificationToFarm />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payroll"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PayrollManagement />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/payslip"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff" || user?.role === "support_staff" || user?.role === "vet_doctor" || user?.role === "auditor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PaySlip />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/forms"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff" || user?.role === "support_staff") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Forms />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/timesheet"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <TimeSheet />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/messages"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Messages />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/directory"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <Directory />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/price"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PriceManagement />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customer-account-records"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "customer" || user?.role === "distributor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerAccountRecords />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/customer-order-pickup"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff" || user?.role === "customer" || user?.role === "distributor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerOrderPickup />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/daily-egg-production"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <DailyEggProductionByPen />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/order-pickup-summary"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "distributor") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <OrderPickupSummary />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/pickup-analytics"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PickupAnalytics />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/sales-analysis"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <SalesAnalysis />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/pen-floor-production"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager" || user?.role === "staff") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PenFloorDailyEggProduction />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/production-reports"
            element={
              isAuthenticated && (user?.role === "admin" || user?.role === "manager") ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ProductionReports />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/productions-edit"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <ProductionsEdit />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/pen-assignments"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <Layout user={user} onLogout={handleLogout}>
                  <PenAssignments />
                </Layout>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          </Routes>
          {/* Show message popup for staff users after login */}
          {isAuthenticated && user?.role === "staff" && <MessagePopup />}
        </BrowserRouter>
      </CurrencyProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;